/* The focusable table region provides keyboard horizontal scrolling on small screens. */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  createPrinter,
  deletePrinter,
  getPrinters,
  updatePrinter,
} from "../../../../../../api/printers";
import {
  getOrderManagers,
  getSalesPoints,
} from "../../../../../../api/configuration";
import "./style.css";

const associations = (printer, points, managers) => [
  ...points
    .filter((p) => p.printerId === printer.printerId)
    .map((p) => ({ platform: "Punto de Venta", name: p.name })),
  ...managers
    .filter((p) => p.printerId === printer.printerId)
    .map((p) => ({ platform: "Gestor de Pedidos", name: p.name })),
];
function PrinterDialog({ children, title, close, busy }) {
  const ref = useRef(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const previous = document.activeElement;
    const panel = ref.current;
    (panel.querySelector("input") || panel.querySelector("button"))?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
      }
      if (e.key !== "Tab") return;
      const nodes = [...panel.querySelectorAll("input,button")].filter(
        (n) => !n.disabled,
      );
      if (!nodes.length) {
        e.preventDefault();
        panel.focus();
        return;
      }
      if (e.shiftKey && document.activeElement === nodes[0]) {
        e.preventDefault();
        nodes[nodes.length - 1].focus();
      } else if (
        !e.shiftKey &&
        document.activeElement === nodes[nodes.length - 1]
      ) {
        e.preventDefault();
        nodes[0].focus();
      }
    };
    panel.addEventListener("keydown", onKey);
    return () => {
      panel.removeEventListener("keydown", onKey);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return createPortal(
    <div className="printerDialogBackdrop">
      <section
        className="printerDialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-busy={busy}
        tabIndex={-1}
        ref={ref}
      >
        <header>
          <h2>{title}</h2>
          <button
            type="button"
            aria-label="Cerrar"
            onClick={close}
            disabled={busy}
          >
            ×
          </button>
        </header>
        {children}
      </section>
    </div>,
    document.body,
  );
}
export const Printers = () => {
  const canManage =
    useSelector((s) => s.aplicationConfig.userConfig.userData?.role) ===
    "admin";
  const [data, setData] = useState({ printers: [], points: [], managers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [draft, setDraft] = useState(null);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const lock = useRef(false);
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.all([
        getPrinters(),
        getSalesPoints(),
        getOrderManagers(),
      ]);
      if (responses.some((r) => !r || r.error || !Array.isArray(r.data)))
        throw new Error();
      setData({
        printers: responses[0].data,
        points: responses[1].data,
        managers: responses[2].data,
      });
      return true;
    } catch {
      setError("No se pudo cargar la configuración de las impresoras.");
      return false;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const open = (printer) => {
    if (!canManage || loading || error) return;
    setFormError("");
    setConfirmDelete(false);
    setUncertain(false);
    setDraft({
      printerId: printer?.printerId,
      name: printer?.name || "",
      ip: printer?.ip || "",
      port: String(printer?.port || 9100),
      pointOfSaleConfigurationIds: data.points
        .filter((p) => p.printerId === printer?.printerId && printer)
        .map((p) => p.PointOfSaleConfigurationId),
      orderManagerConfigurationIds: data.managers
        .filter((p) => p.printerId === printer?.printerId && printer)
        .map((p) => p.orderManagerConfigurationId),
    });
  };
  const close = () => {
    if (!lock.current) setDraft(null);
  };
  const change = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const toggle = (key, id) =>
    change(
      key,
      draft[key].includes(id)
        ? draft[key].filter((v) => v !== id)
        : [...draft[key], id],
    );
  const save = async (e) => {
    e.preventDefault();
    if (!canManage || lock.current || uncertain) return;
    const port = Number(draft.port);
    if (
      !draft.name.trim() ||
      !draft.ip.trim() ||
      !Number.isInteger(port) ||
      port < 1 ||
      port > 65535
    ) {
      setFormError(
        "Introduce nombre, dirección IP y un puerto entre 1 y 65535.",
      );
      return;
    }
    lock.current = true;
    setBusy(true);
    setFormError("");
    try {
      const payload = {
        name: draft.name.trim(),
        ip: draft.ip.trim(),
        port,
        pointOfSaleConfigurationIds: draft.pointOfSaleConfigurationIds,
        orderManagerConfigurationIds: draft.orderManagerConfigurationIds,
      };
      const response = draft.printerId
        ? await updatePrinter(draft.printerId, payload)
        : await createPrinter(payload);
      if (!response) throw new Error();
      if (response.error) {
        setFormError(response.message || "No se pudo guardar la impresora.");
        return;
      }
      setDraft(null);
      setNotice("Impresora guardada.");
      await load();
    } catch {
      setUncertain(true);
      setFormError(
        "No se pudo confirmar el guardado. Cierra y actualiza el listado antes de volver a intentarlo.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!canManage || lock.current || !confirmDelete || uncertain) return;
    lock.current = true;
    setBusy(true);
    setFormError("");
    try {
      const response = await deletePrinter(draft.printerId);
      if (!response) throw new Error();
      if (response.error) {
        setFormError(response.message || "No se pudo eliminar la impresora.");
        return;
      }
      setDraft(null);
      setNotice("Impresora eliminada.");
      await load();
    } catch {
      setUncertain(true);
      setFormError(
        "No se pudo confirmar la eliminación. Cierra y actualiza el listado antes de volver a intentarlo.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return (
    <div className="printersPage">
      <div className="printersPanel">
        <header className="printersHeading">
          <div>
            <h3>Impresoras del negocio</h3>
            <p>Cada impresora se asocia al perfil de una plataforma.</p>
          </div>
          {canManage && (
            <button
              type="button"
              className="printerPrimary"
              disabled={loading || !!error}
              onClick={() => open(null)}
            >
              ＋ Nueva impresora
            </button>
          )}
        </header>
        {notice && (
          <p className="printerNotice" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="printerError" role="alert">
            {error}{" "}
            <button type="button" onClick={load} disabled={loading}>
              Actualizar listado
            </button>
          </p>
        )}
        {loading ? (
          <p className="printersEmpty" role="status">
            Cargando impresoras…
          </p>
        ) : (
          !error && (
            <>
              <div
                className="printersTableScroll"
                role="region"
                aria-label="Impresoras del negocio"
                tabIndex={0}
              >
                <table className="printersTable">
                  <thead>
                    <tr>
                      {[
                        "Nombre",
                        "Dirección IP",
                        "Puerto",
                        "Plataforma",
                        "Dispositivo",
                        ...(canManage ? ["Acciones"] : []),
                      ].map((label) => (
                        <th key={label} scope="col">
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.printers.map((printer) => {
                      const links = associations(
                        printer,
                        data.points,
                        data.managers,
                      );
                      return (
                        <tr key={printer.printerId}>
                          <th scope="row">{printer.name}</th>
                          <td className="printerMono">{printer.ip}</td>
                          <td className="printerMono">{printer.port}</td>
                          <td>
                            {links.length
                              ? links.map((link, i) => (
                                  <div key={i}>{link.platform}</div>
                                ))
                              : "Sin asociar"}
                          </td>
                          <td>
                            {links.length
                              ? links.map((link, i) => (
                                  <div key={i}>{link.name}</div>
                                ))
                              : "—"}
                          </td>
                          {canManage && (
                            <td>
                              <button
                                type="button"
                                aria-label={`Editar ${printer.name}`}
                                onClick={() => open(printer)}
                              >
                                Editar
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!data.printers.length && (
                <p className="printersEmpty">
                  Todavía no hay impresoras configuradas.
                </p>
              )}
            </>
          )
        )}
      </div>
      {draft && (
        <PrinterDialog
          title={draft.printerId ? "Editar impresora" : "Nueva impresora"}
          close={close}
          busy={busy}
        >
          <form onSubmit={save}>
            <div className="printerFormBody">
              <fieldset disabled={busy || uncertain}>
                <legend className="printerSrOnly">Datos de la impresora</legend>
                <label>
                  Nombre
                  <input
                    autoComplete="off"
                    value={draft.name}
                    maxLength={255}
                    required
                    onChange={(e) => change("name", e.target.value)}
                  />
                </label>
                <div className="printerFields">
                  <label>
                    Dirección IP
                    <input
                      value={draft.ip}
                      required
                      placeholder="192.168.1.50"
                      onChange={(e) => change("ip", e.target.value)}
                    />
                  </label>
                  <label>
                    Puerto
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      step="1"
                      required
                      value={draft.port}
                      onChange={(e) => change("port", e.target.value)}
                    />
                  </label>
                </div>
                {[
                  {
                    title: "Punto de Venta",
                    rows: data.points,
                    key: "pointOfSaleConfigurationIds",
                    id: "PointOfSaleConfigurationId",
                  },
                  {
                    title: "Gestor de Pedidos",
                    rows: data.managers,
                    key: "orderManagerConfigurationIds",
                    id: "orderManagerConfigurationId",
                  },
                ].map((group) => (
                  <fieldset className="printerDevices" key={group.key}>
                    <legend>{group.title}</legend>
                    {group.rows.length ? (
                      group.rows.map((row) => (
                        <label key={row[group.id]}>
                          <input
                            type="checkbox"
                            checked={draft[group.key].includes(row[group.id])}
                            onChange={() => toggle(group.key, row[group.id])}
                          />
                          <span>
                            {row.name}
                            {row.printerId &&
                              row.printerId !== draft.printerId && (
                                <small>
                                  Asociado a otra impresora. Al guardar se
                                  cambiará esta asociación.
                                </small>
                              )}
                          </span>
                        </label>
                      ))
                    ) : (
                      <p>No hay dispositivos de esta plataforma.</p>
                    )}
                  </fieldset>
                ))}
              </fieldset>
              {formError && (
                <p className="printerError" role="alert">
                  {formError}
                </p>
              )}
              {confirmDelete && (
                <div className="printerDeleteConfirm">
                  <p>
                    ¿Eliminar «{draft.name}»? Sus dispositivos quedarán sin
                    impresora.
                  </p>
                  <button
                    type="button"
                    disabled={busy || uncertain}
                    onClick={remove}
                  >
                    Confirmar eliminación
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setConfirmDelete(false)}
                  >
                    Volver
                  </button>
                </div>
              )}
            </div>
            <footer>
              {draft.printerId && !confirmDelete && (
                <button
                  type="button"
                  className="printerDanger"
                  disabled={busy || uncertain}
                  onClick={() => setConfirmDelete(true)}
                >
                  Eliminar impresora
                </button>
              )}
              <span />
              <button type="button" onClick={close} disabled={busy}>
                {uncertain ? "Cerrar" : "Cancelar"}
              </button>
              <button
                type="submit"
                className="printerPrimary"
                disabled={busy || uncertain || confirmDelete}
              >
                {busy ? "Guardando…" : "Guardar"}
              </button>
            </footer>
          </form>
        </PrinterDialog>
      )}
    </div>
  );
};
